import { useState } from "react";

const SURFACE = "#141412";
const BORDER = "#242220";
const TEXT = "#edeae2";
const MUTED = "#8a8680";
const DIM = "#585450";
const BG = "#000";
// Neutral blue treatment — matches C.blue/C.blueBg/C.blueBorder used for the
// Reminders/Spend Log accordion rows on the My Gear/Instruments card (App.js).
const NEUTRAL_BLUE = "#6aaee0";
const NEUTRAL_BLUE_BG = "#0d1822";
const NEUTRAL_BLUE_BORDER = "#1c3148";

// ── Physical viewing checklist ─────────────────────────────────────────────────
const GENERIC_CHECKLIST = [
  { id: "neck_straight", label: "Neck: sight down for bow or twist" },
  { id: "frets_level", label: "Frets: check for wear grooves, high frets" },
  { id: "nut_condition", label: "Nut: no cracks, slots not too deep or high" },
  { id: "tuners_solid", label: "Tuners: turn smoothly, no wobble" },
  { id: "truss_rod", label: "Truss rod: adjusts freely (test cautiously)" },
  { id: "electronics", label: "Electronics: all controls clean, no crackling" },
  { id: "output_jack", label: "Output jack: secure, no crackle" },
  { id: "finish_check", label: "Finish: note any cracks, chips, repairs" },
  { id: "headstock_check", label: "Headstock: check for repair history" },
  { id: "serial_number", label: "Serial number: verify against listing" },
  { id: "play_test", label: "Play test: every fret on every string" },
  { id: "intonation", label: "Intonation: 12th fret harmonic vs fretted" },
];

const TYPE_SPECIFIC = {
  "Bass Guitar": [
    { id: "neck_dive", label: "Neck dive: check balance while strapped" },
    { id: "active_battery", label: "Active pickups: test battery, all EQ bands" },
    { id: "string_spacing", label: "String spacing: suits your playing style" },
  ],
  "Electric Guitar": [
    { id: "tremolo_setup", label: "Tremolo/bridge: level, returns to pitch" },
    { id: "pickup_height", label: "Pickup height: consistent output per pickup" },
    { id: "coil_tap", label: "Coil tap / switching: all positions work cleanly" },
  ],
  "Acoustic Guitar": [
    { id: "top_belly", label: "Top: no belly bulge behind bridge" },
    { id: "bracing", label: "Bracing: check for loose braces (rattle)" },
    { id: "saddle_height", label: "Saddle height: action not excessively high" },
  ],
};

function verdictColor(word, accent) {
  if (!word) return TEXT;
  const w = (word || "").toLowerCase();
  if (w.includes("buy")) return "#34c759";
  if (w.includes("pass") || w.includes("avoid")) return "#ef4444";
  return accent || "#E8621A";
}
function safeParseEval(str) {
  try { const r = JSON.parse(str); return r && typeof r === "object" ? r : null; } catch(e) { return null; }
}
function safeParseUrls(str) {
  try { const r = JSON.parse(str); return Array.isArray(r) ? r : []; } catch(e) { return []; }
}
function confidenceColor(s) {
  if (s >= 80) return "#34c759";
  if (s >= 60) return "#f59e0b";
  return "#ef4444";
}
function conditionColor(c) {
  return { Excellent: "#34c759", Good: "#7ecb8f", Fair: "#f59e0b", Poor: "#ef4444" }[c] || TEXT;
}
function getListingMeta(url) {
  try {
    const hostname = new URL(url).hostname;
    const iconUrl =
      `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
    if (hostname.includes('facebook.com'))
      return { label: 'View on Facebook', iconUrl };
    if (hostname.includes('trademe.co.nz'))
      return { label: 'View on Trade Me', iconUrl };
    if (hostname.includes('reverb.com'))
      return { label: 'View on Reverb', iconUrl };
    if (hostname.includes('gumtree.com.au'))
      return { label: 'View on Gumtree', iconUrl };
    if (hostname.includes('ebay.'))
      return { label: 'View on eBay', iconUrl };
    return { label: 'View Listing', iconUrl };
  } catch {
    return { label: 'View Listing', iconUrl: null };
  }
}

// ── Shared input style ─────────────────────────────────────────────────────────
const inputSt = {
  width: "100%", background: "#0a0a0a", border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: "10px 12px", color: TEXT, fontSize: 13, fontFamily: "inherit",
  outline: "none", lineHeight: 1.5, boxSizing: "border-box",
};

// ── Collapsible section header ─────────────────────────────────────────────────
function SectionBar({ icon, label, open, onToggle, orange, action, badges }) {
  return (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", padding: "11px 12px", cursor: "pointer", gap: 8 }}>
      {icon}
      <div style={{ fontSize: 11, fontWeight: 600, color: orange, textTransform: "uppercase", letterSpacing: "0.08em", flex: 1 }}>{label}</div>
      {badges}
      {action && <span onClick={e => e.stopPropagation()}>{action}</span>}
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={DIM} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ flexShrink: 0, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}>
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
  );
}

// ── Clarification section ─────────────────────────────────────────────────────
function ClarificationSection({ questions, onReassess, ORANGE, BORDER, DIM, MUTED, TEXT, inputSt }) {
  const [answers, setAnswers] = useState("");
  return (
    <div style={{ background: "#0d1a10", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        Confidence Low — Answer to Improve
      </div>
      {questions.map((q, i) => (
        <div key={i} style={{ fontSize: 13, color: MUTED, marginBottom: 6, paddingLeft: 10, borderLeft: `2px solid rgba(245,158,11,0.4)` }}>{q}</div>
      ))}
      <textarea
        value={answers}
        onChange={e => setAnswers(e.target.value)}
        rows={3}
        placeholder="Type your answers here…"
        style={{ ...inputSt, marginTop: 10, resize: "vertical", fontSize: 12 }}
      />
      <button
        onClick={() => onReassess(answers)}
        disabled={!answers.trim()}
        style={{ marginTop: 8, width: "100%", padding: "9px", borderRadius: 8, border: `1px solid rgba(245,158,11,0.4)`, background: answers.trim() ? "rgba(245,158,11,0.1)" : "transparent", color: answers.trim() ? "#f59e0b" : DIM, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: answers.trim() ? "pointer" : "not-allowed" }}
      >
        Re-evaluate with Answers
      </button>
    </div>
  );
}

// ── Watchlist card (inline expand) ────────────────────────────────────────────
function WatchlistItem({ item, onBuy, onRemove, onUpdate, onOpenGallery, accentColor, accentRgb, market, isExpanded, onToggleExpand }) {
  const ORANGE = accentColor || "#E8621A";
  const ACCENT_RGB = accentRgb || "232,98,26";
  const imageUrls = safeParseUrls(item.image_urls);
  const instrument = item.instrument || "Bass Guitar";
  const typeChecklist = TYPE_SPECIFIC[instrument] || [];
  const allCheckItems = [...GENERIC_CHECKLIST, ...typeChecklist];
  const listingMeta = item.listing_url ? getListingMeta(item.listing_url) : null;
  const displayModel = (item.model || "Unknown").split("(")[0].trim() || "Unknown";

  // Local evaluate_result so reassess can update it without parent re-mount
  const [localEvalStr, setLocalEvalStr] = useState(item.evaluate_result || "");
  const evalData = safeParseEval(localEvalStr);

  const expanded = isExpanded;
  const [checklistDone, setChecklistDone] = useState(() => {
    try { return JSON.parse(item.checklist_done || "{}"); } catch(e) { return {}; }
  });

  // Section open states
  const [aiOpen, setAiOpen] = useState(false);
  const [reportEditing, setReportEditing] = useState(false);
  const [reportText, setReportText] = useState(evalData?.verdictText || evalData?.model_overview?.era_notes || "");
  const [sellerOpen, setSellerOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [findingsOpen, setFindingsOpen] = useState(false);

  // Ask the seller + answers
  const [allCopied, setAllCopied] = useState(false);
  const [sellerAnswers, setSellerAnswers] = useState(item.seller_answers || "");
  const [answersEditing, setAnswersEditing] = useState(false);

  // Findings / negotiation
  const [negotiatedPrice, setNegotiatedPrice] = useState(item.negotiated_price || "");
  const [inspectionNotes, setInspectionNotes] = useState(item.inspection_notes || "");
  const [findingsSaving, setFindingsSaving] = useState(false);

  // Reassess
  const [reassessing, setReassessing] = useState(false);
  const [reassessError, setReassessError] = useState("");

  // AI Report section editing
  const [evalEditField, setEvalEditField] = useState(null); // { field, value }
  const [evalFieldSaving, setEvalFieldSaving] = useState(false);

  const saveEvalSection = async (fieldPath, newValue) => {
    setEvalFieldSaving(true);
    try {
      const updated = JSON.parse(JSON.stringify(evalData || {}));
      const parts = fieldPath.split('.');
      let cur = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!cur[parts[i]]) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = newValue;
      const newJson = JSON.stringify(updated);
      setLocalEvalStr(newJson);
      onUpdate(item.id, { evaluate_result: newJson });
      setEvalEditField(null);
    } catch(e) { console.error('watchlist eval save error:', e); }
    setEvalFieldSaving(false);
  };

  // Buy sheet
  const [buyDestOpen, setBuyDestOpen] = useState(false);
  const [buyPrice, setBuyPrice] = useState(item.asking_price ? String(item.asking_price) : "");
  const [buyDate, setBuyDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [removeLoading, setRemoveLoading] = useState(false);

  const tickedCount = allCheckItems.filter(c => checklistDone[c.id]).length;

  const tickItem = (id) => {
    const next = { ...checklistDone, [id]: !checklistDone[id] };
    setChecklistDone(next);
    onUpdate(item.id, { checklist_done: JSON.stringify(next) });
  };

  const saveReport = () => {
    const updated = JSON.stringify({ ...evalData, verdictText: reportText });
    setLocalEvalStr(updated);
    onUpdate(item.id, { evaluate_result: updated });
    setReportEditing(false);
  };

  const copyAll = () => {
    // Support both old (questions) and new (seller_questions) formats
    const questions = evalData?.seller_questions || evalData?.questions || [];
    if (!questions.length) return;
    const text = questions.map((q, i) => `${i + 1}. ${q}`).join("\n\n");
    const doCopy = (t) => {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(t).then(() => { setAllCopied(true); setTimeout(() => setAllCopied(false), 2500); }).catch(() => fallback(t));
      } else { fallback(t); }
    };
    const fallback = (t) => {
      const el = document.createElement("textarea"); el.value = t; el.style.position = "fixed"; el.style.opacity = "0";
      document.body.appendChild(el); el.select(); try { document.execCommand("copy"); } catch(e) {}
      document.body.removeChild(el); setAllCopied(true); setTimeout(() => setAllCopied(false), 2500);
    };
    doCopy(text);
  };

  const saveAnswers = () => {
    onUpdate(item.id, { seller_answers: sellerAnswers });
    setAnswersEditing(false);
  };

  const saveFindings = async () => {
    setFindingsSaving(true);
    onUpdate(item.id, { negotiated_price: negotiatedPrice, inspection_notes: inspectionNotes });
    setTimeout(() => setFindingsSaving(false), 800);
  };

  const handleReassess = async () => {
    setReassessing(true);
    setReassessError("");
    try {
      const fd = new FormData();
      fd.append("intent", "buy_self");
      fd.append("makeModel", item.model || "");
      fd.append("price", String(item.asking_price || ""));
      fd.append("notes", [sellerAnswers, inspectionNotes].filter(Boolean).join("\n\n"));
      if (item.image_urls) {
        fd.append("existing_image_urls", item.image_urls);
      }
      const r = await fetch("/api/evaluate/assess", { method: "POST", body: fd });
      const data = await r.json();
      if (data.error) { setReassessError(data.error); return; }
      // Store the full new result as evaluate_result JSON
      const evalJson = JSON.stringify(data);
      setLocalEvalStr(evalJson);
      onUpdate(item.id, { evaluate_result: evalJson });
      setAiOpen(true);
    } catch(e) {
      setReassessError("Network error — check connection.");
    }
    setReassessing(false);
  };

  const doReassessWithAnswers = (answers) => {
    setReassessing(true);
    setReassessError("");
    const fd = new FormData();
    fd.append("intent", "buy_self");
    fd.append("makeModel", item.model || "");
    fd.append("price", String(item.asking_price || ""));
    fd.append("clarification_answers", answers);
    if (item.image_urls) fd.append("existing_image_urls", item.image_urls);
    fetch("/api/evaluate/assess", { method: "POST", body: fd })
      .then(r => r.json())
      .then(data => {
        if (data.error) { setReassessError(data.error); return; }
        const evalJson = JSON.stringify(data);
        setLocalEvalStr(evalJson);
        onUpdate(item.id, { evaluate_result: evalJson });
        setAiOpen(true);
      })
      .catch(() => setReassessError("Network error."))
      .finally(() => setReassessing(false));
  };

  // Star icon for AI Report
  const StarIcon = (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );
  const ChatIcon = (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={NEUTRAL_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
  const CheckIcon = (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={NEUTRAL_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  );
  const FindingsIcon = (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={NEUTRAL_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );

  const sectionBox = { background: "#111", border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 10 };

  // New format detection
  const isNewFormat = evalData && (evalData.model_overview || evalData.condition || evalData.confidence_score != null);
  const sellerQuestions = evalData?.seller_questions || evalData?.questions || [];
  const currency = evalData?.market_value?.currency || "NZD";

  // Offer suggestion — suppressed for new (non-secondhand) listings
  const rawSuggestedOffer = evalData?.market_value?.suggested_offer != null ? evalData.market_value.suggested_offer : null;
  const offerSuggestionDisplay = item.new_or_used === "New" ? null
    : rawSuggestedOffer != null ? `${currency} ${rawSuggestedOffer}`
    : evalData?.marketValue?.suggestedOffer || null;
  const showListed = item.asking_price && String(item.asking_price) !== String(rawSuggestedOffer);

  return (
    <div style={{ background: SURFACE, border: `1px solid rgba(${ACCENT_RGB},0.35)`, borderRadius: 14, marginBottom: 10, overflow: "hidden" }}>

      {/* ── Card header — always visible ── */}
      <div
        onClick={() => onToggleExpand()}
        style={{ width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 8, padding: "12px 16px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: TEXT, lineHeight: 1.3, width: "100%", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayModel}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", width: "100%" }}>
          <div style={{ position: "relative", flexShrink: 0 }} onClick={imageUrls.length > 0 ? (e) => { e.stopPropagation(); onOpenGallery(item); } : undefined}>
            <div style={{ width: 64, height: 64, borderRadius: 10, background: "#1a1a18", border: `1px solid ${BORDER}`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {(item.hero_image_url || imageUrls[0])
                ? <img src={item.hero_image_url || imageUrls[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke={DIM} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              }
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
            <div style={{ fontSize: 12, color: TEXT }}>{item.instrument}</div>
            {(showListed || offerSuggestionDisplay) && (
              <div style={{ fontSize: 11, marginTop: 2 }}>
                {showListed && <span style={{ color: ORANGE, fontWeight: 600 }}>Listed: ${item.asking_price}</span>}
                {showListed && offerSuggestionDisplay && <span style={{ color: MUTED }}> · </span>}
                {offerSuggestionDisplay && <span style={{ color: "#5ab870", fontWeight: 600 }}>Offer: {offerSuggestionDisplay}</span>}
              </div>
            )}
            {/* Confidence score badge (new format) */}
            {evalData?.confidence_score != null && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px",
                borderRadius: 12, fontSize: 11, fontWeight: 600, marginTop: 4,
                background: evalData.confidence_score >= 80 ? "rgba(52,199,89,0.15)" : evalData.confidence_score >= 60 ? "rgba(245,158,11,0.15)" : "rgba(239,68,68,0.15)",
                color: confidenceColor(evalData.confidence_score),
              }}>
                {evalData.confidence_score}% confidence
              </div>
            )}
            {/* Legacy verdict word (old format) */}
            {evalData?.verdictWord && !isNewFormat && (
              <div style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: verdictColor(evalData.verdictWord, ORANGE), flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: verdictColor(evalData.verdictWord, ORANGE), fontWeight: 600 }}>{evalData.verdictWord}</span>
              </div>
            )}
            {item.listing_url && (
              <a href={item.listing_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                style={{ fontSize: 11, color: "#6aaee0", textDecoration: "none", display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                {listingMeta.iconUrl ? (
                  <img src={listingMeta.iconUrl} alt="" width={16} height={16} onError={e => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                )}
                {listingMeta.label}
              </a>
            )}
          </div>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={MUTED} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 16px 16px" }}>

          {/* Hero image full-width */}
          {imageUrls.length > 0 && (
            <div onClick={() => onOpenGallery(item)} style={{ marginBottom: 12, cursor: "pointer", position: "relative" }}>
              <img src={item.hero_image_url || imageUrls[0]} alt=""
                style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 10, border: `1px solid ${BORDER}`, display: "block" }} />
            </div>
          )}

          {/* ── Clarification questions (new format, low confidence) ── */}
          {evalData?.clarification_questions?.length > 0 && evalData.confidence_score < 80 && (
            <ClarificationSection
              questions={evalData.clarification_questions}
              onReassess={doReassessWithAnswers}
              ORANGE={ORANGE} BORDER={BORDER} DIM={DIM} MUTED={MUTED} TEXT={TEXT} inputSt={inputSt}
            />
          )}

          {/* ── AI Report ── */}
          <div style={{ ...sectionBox, border: `1px solid rgba(${ACCENT_RGB},0.25)` }}>
            <SectionBar
              icon={StarIcon} label="AI Report" open={aiOpen} onToggle={() => setAiOpen(p => !p)} orange={ORANGE}
              badges={evalData && <>
                {evalData.confidence_score != null && (() => { const cc = confidenceColor(evalData.confidence_score); return <span style={{ fontSize:10, fontWeight:700, color:cc, background:`${cc}18`, padding:"2px 7px", borderRadius:10, border:`1px solid ${cc}30` }}>{evalData.confidence_score}%</span>; })()}
                {evalData.condition?.overall && (() => { const co = evalData.condition.overall; const dc = {Excellent:"#34c759",Good:"#7ecb8f",Fair:"#f59e0b",Poor:"#ef4444"}[co]; return <span style={{ fontSize:10, fontWeight:700, color:dc, background:`${dc}18`, padding:"2px 7px", borderRadius:10, border:`1px solid ${dc}30`, marginLeft:4 }}>{co}</span>; })()}
              </>}
              action={aiOpen && !isNewFormat && (reportEditing
                ? <button onClick={e => { e.stopPropagation(); saveReport(); }} style={{ fontSize: 11, fontWeight: 700, color: "#34c759", background: "rgba(52,199,89,0.1)", border: "1px solid rgba(52,199,89,0.3)", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                : <button onClick={e => { e.stopPropagation(); setReportEditing(true); }} style={{ fontSize: 11, color: DIM, background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
              )}
            />
            {aiOpen && (
              <div style={{ borderTop: `1px solid ${BORDER}`, padding: "0 12px 12px", paddingTop: 10 }}>
                {isNewFormat ? (
                  <div>
                    {/* Confidence score */}
                    {evalData.confidence_score != null && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 16, marginBottom: 10,
                        background: evalData.confidence_score >= 80 ? "rgba(52,199,89,0.12)" : evalData.confidence_score >= 60 ? "rgba(245,158,11,0.12)" : "rgba(239,68,68,0.12)",
                        border: `1px solid ${confidenceColor(evalData.confidence_score)}40`,
                      }}>
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: confidenceColor(evalData.confidence_score) }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: confidenceColor(evalData.confidence_score) }}>{evalData.confidence_score}% confidence</span>
                      </div>
                    )}
                    {/* Model overview — editable */}
                    {evalEditField?.field === 'model_overview.era_notes' ? (
                      <div style={{ marginBottom: 8 }}>
                        <textarea value={evalEditField.value} onChange={e => setEvalEditField(p => ({ ...p, value: e.target.value }))} rows={3} style={{ width: "100%", background: "#0a0a0a", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px", color: TEXT, fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", marginBottom: 6, minHeight: 120 }} />
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => saveEvalSection('model_overview.era_notes', evalEditField.value)} disabled={evalFieldSaving} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: "#C24E10", color: "#fff", fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>Save</button>
                          <button onClick={() => setEvalEditField(null)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${BORDER}`, background: "transparent", color: MUTED, fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ position: "relative", paddingRight: 44, marginBottom: 8 }}>
                        {evalData.model_overview?.era_notes && <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>{evalData.model_overview.era_notes}</div>}
                        <button onClick={() => setEvalEditField({ field: 'model_overview.era_notes', value: evalData.model_overview?.era_notes || '' })} style={{ position: "absolute", top: 0, right: 0, background: "none", border: `1px solid ${BORDER}`, color: DIM, borderRadius: 6, padding: "5px 11px", fontSize: 16, cursor: "pointer", fontFamily: "inherit", lineHeight: 1 }}>✎</button>
                      </div>
                    )}
                    {/* Hardware */}
                    {evalData.hardware_overview && (evalData.hardware_overview.pickups || evalData.hardware_overview.bridge) && (
                      <div style={{ fontSize: 12, color: DIM, marginBottom: 8 }}>
                        {[evalData.hardware_overview.pickups, evalData.hardware_overview.bridge, evalData.hardware_overview.notable_specs].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    {/* Condition */}
                    {evalData.condition?.overall && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 12, marginBottom: 8,
                        background: `${conditionColor(evalData.condition.overall)}18`,
                        border: `1px solid ${conditionColor(evalData.condition.overall)}40`,
                      }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: conditionColor(evalData.condition.overall) }}>{evalData.condition.overall}</span>
                      </div>
                    )}
                    {/* Condition flags */}
                    {evalData.condition?.flags?.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {evalData.condition.flags.map((f, i) => (
                          <div key={i} style={{ fontSize: 12, color: MUTED, display: "flex", gap: 6, marginBottom: 4 }}>
                            <span style={{ color: "#f59e0b" }}>⚑</span>{f}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Setup quirks — editable */}
                    {evalEditField?.field === 'setup_profile.known_quirks' ? (
                      <div style={{ marginBottom: 8 }}>
                        <textarea value={evalEditField.value} onChange={e => setEvalEditField(p => ({ ...p, value: e.target.value }))} rows={2} style={{ width: "100%", background: "#0a0a0a", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px", color: TEXT, fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", marginBottom: 6, minHeight: 120 }} />
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => saveEvalSection('setup_profile.known_quirks', evalEditField.value)} disabled={evalFieldSaving} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: "#C24E10", color: "#fff", fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>Save</button>
                          <button onClick={() => setEvalEditField(null)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${BORDER}`, background: "transparent", color: MUTED, fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                        </div>
                      </div>
                    ) : evalData.setup_profile?.known_quirks ? (
                      <div style={{ position: "relative", paddingRight: 44, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: DIM, fontStyle: "italic" }}>{evalData.setup_profile.known_quirks}</div>
                        <button onClick={() => setEvalEditField({ field: 'setup_profile.known_quirks', value: evalData.setup_profile.known_quirks || '' })} style={{ position: "absolute", top: 0, right: 0, background: "none", border: `1px solid ${BORDER}`, color: DIM, borderRadius: 6, padding: "5px 11px", fontSize: 16, cursor: "pointer", fontFamily: "inherit", lineHeight: 1 }}>✎</button>
                      </div>
                    ) : null}
                    {/* Market value */}
                    {evalData.market_value && (evalData.market_value.fair_range_low || evalData.market_value.suggested_offer) && (
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
                        {evalData.market_value.fair_range_low && evalData.market_value.fair_range_high && (
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                            <span style={{ fontSize: 12, color: DIM }}>Fair range</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: ORANGE }}>{currency} {evalData.market_value.fair_range_low}–{evalData.market_value.fair_range_high}</span>
                          </div>
                        )}
                        {evalData.market_value.suggested_offer && (
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                            <span style={{ fontSize: 12, color: DIM }}>Suggested offer</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#5ab870" }}>{currency} {evalData.market_value.suggested_offer}</span>
                          </div>
                        )}
                        {evalData.intent === "flip" && evalData.market_value.flip_margin_estimate && (
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                            <span style={{ fontSize: 12, color: DIM }}>Flip margin</span>
                            <span style={{ fontSize: 12, color: MUTED }}>{currency} {evalData.market_value.flip_margin_estimate}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {reportEditing ? (
                      <textarea value={reportText} onChange={e => setReportText(e.target.value)} rows={5} autoFocus style={{ ...inputSt, resize: "vertical" }} />
                    ) : (
                      <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.7 }}>
                        {reportText || <span style={{ color: DIM, fontStyle: "italic" }}>No AI report yet. Run Evaluate first.</span>}
                      </div>
                    )}
                    {evalData?.marketValue && (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
                        {evalData.marketValue.fairRange && <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><span style={{ fontSize: 12, color: DIM }}>Fair range</span><span style={{ fontSize: 12, fontWeight: 600, color: ORANGE }}>{evalData.marketValue.fairRange}</span></div>}
                        {evalData.marketValue.suggestedOffer && <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><span style={{ fontSize: 12, color: DIM }}>Suggested offer</span><span style={{ fontSize: 12, fontWeight: 600, color: "#5ab870" }}>{evalData.marketValue.suggestedOffer}</span></div>}
                        {/* verdictWord holds the intent string for legacy-format records (buildEvalData sets verdictWord: results?.intent), not an actual verdict — confirmed before wiring this gate */}
                        {evalData.verdictWord === "flip" && evalData.marketValue.flipMargin && <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><span style={{ fontSize: 12, color: DIM }}>Flip margin</span><span style={{ fontSize: 12, color: MUTED }}>{evalData.marketValue.flipMargin}</span></div>}
                      </div>
                    )}
                    {evalData?.flags?.length > 0 && (
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
                        {evalData.flags.map((f, i) => (
                          <div key={i} style={{ fontSize: 12, color: f.type === "warn" ? "#d97706" : MUTED, display: "flex", gap: 6, marginBottom: 4 }}>
                            <span>{f.type === "ok" ? "✓" : "⚑"}</span>
                            <span>{typeof f === "string" ? f : f.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Ask the Seller + Answers ── */}
          {sellerQuestions.length > 0 && (
            <div style={{ ...sectionBox, background: NEUTRAL_BLUE_BG, border: `1px solid ${NEUTRAL_BLUE_BORDER}` }}>
              <SectionBar
                icon={ChatIcon} label="Ask the Seller" open={sellerOpen} onToggle={() => setSellerOpen(p => !p)} orange={NEUTRAL_BLUE}
                action={sellerOpen && (
                  <button
                    onClick={e => { e.stopPropagation(); copyAll(); }}
                    style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: allCopied ? "#34c759" : DIM, background: "none", border: `1px solid ${allCopied ? "#34c759" : BORDER}`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", transition: "color 0.2s, border-color 0.2s" }}
                  >
                    {allCopied ? (
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    )}
                    {allCopied ? "Copied!" : "Copy All"}
                  </button>
                )}
              />
              {sellerOpen && (
                <div style={{ borderTop: `1px solid ${BORDER}`, padding: "10px 12px 14px" }}>
                  <div style={{ fontSize: 11, color: DIM, marginBottom: 10 }}>Tap Copy All to paste into TradeMe, Gumtree, or Facebook Marketplace.</div>
                  <div style={{ background: "#0a0a0a", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
                    {sellerQuestions.map((q, i) => (
                      <div key={i} style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, paddingBottom: i < sellerQuestions.length - 1 ? 8 : 0, marginBottom: i < sellerQuestions.length - 1 ? 8 : 0, borderBottom: i < sellerQuestions.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                        <span style={{ color: DIM, marginRight: 6 }}>{i + 1}.</span>{q}
                      </div>
                    ))}
                  </div>

                  {/* Answers sub-section */}
                  <div style={{ fontSize: 11, fontWeight: 600, color: ORANGE, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>Answers</span>
                    {!answersEditing
                      ? <button onClick={() => setAnswersEditing(true)} style={{ fontSize: 11, color: DIM, background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "2px 10px", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                      : <button onClick={saveAnswers} style={{ fontSize: 11, fontWeight: 700, color: "#34c759", background: "rgba(52,199,89,0.1)", border: "1px solid rgba(52,199,89,0.3)", borderRadius: 6, padding: "2px 10px", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                    }
                  </div>
                  {answersEditing ? (
                    <textarea
                      value={sellerAnswers} onChange={e => setSellerAnswers(e.target.value)}
                      autoFocus rows={5}
                      placeholder="Paste or type the seller's responses here…"
                      style={{ ...inputSt, resize: "vertical" }}
                    />
                  ) : (
                    <div style={{ background: "#0a0a0a", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px", minHeight: 52 }}>
                      {sellerAnswers
                        ? <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{sellerAnswers}</div>
                        : <div style={{ fontSize: 12, color: DIM, fontStyle: "italic" }}>No answers recorded yet. Tap Edit to add.</div>
                      }
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Findings & Negotiation ── */}
          <div style={{ ...sectionBox, background: NEUTRAL_BLUE_BG, border: `1px solid ${NEUTRAL_BLUE_BORDER}` }}>
            <SectionBar
              icon={FindingsIcon} label="Findings & Negotiation" open={findingsOpen} onToggle={() => setFindingsOpen(p => !p)} orange={NEUTRAL_BLUE}
            />
            {findingsOpen && (
              <div style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 12px 14px" }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: DIM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Negotiated Price</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: DIM }}>$</span>
                    <input
                      type="number" value={negotiatedPrice} onChange={e => setNegotiatedPrice(e.target.value)}
                      placeholder={item.asking_price || "e.g. 450"}
                      style={{ ...inputSt, flex: 1 }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: DIM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Inspection Notes</div>
                  <textarea
                    value={inspectionNotes} onChange={e => setInspectionNotes(e.target.value)}
                    rows={4}
                    placeholder={"Is this a special or limited release?\nAdd findings from physical inspection…\nAny concerns with the neck, frets, or electronics?\nRepairs or modifications noticed?"}
                    style={{ ...inputSt, resize: "vertical" }}
                  />
                </div>
                <button
                  onClick={saveFindings}
                  style={{ width: "100%", padding: "9px", borderRadius: 8, border: `1px solid ${BORDER}`, background: "transparent", color: findingsSaving ? "#34c759" : DIM, fontFamily: "inherit", fontSize: 12, cursor: "pointer", transition: "color 0.2s" }}
                >
                  {findingsSaving ? "Saved ✓" : "Save Findings"}
                </button>
              </div>
            )}
          </div>

          {/* ── Physical Checklist ── */}
          <div style={{ ...sectionBox, background: NEUTRAL_BLUE_BG, border: `1px solid ${NEUTRAL_BLUE_BORDER}` }}>
            <SectionBar
              icon={CheckIcon} label={`Physical Checklist · ${tickedCount}/${allCheckItems.length}`} open={checklistOpen} onToggle={() => setChecklistOpen(p => !p)} orange={NEUTRAL_BLUE}
            />
            {checklistOpen && (
              <div style={{ borderTop: `1px solid ${BORDER}`, padding: "4px 12px 10px" }}>
                {allCheckItems.map((ci) => {
                  const isTypeSpecific = typeChecklist.some(t => t.id === ci.id);
                  return (
                    <div key={ci.id}>
                      {isTypeSpecific && ci.id === typeChecklist[0]?.id && <div style={{ height: 1, background: BORDER, margin: "6px 0" }} />}
                      <button onClick={() => tickItem(ci.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 0", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                        <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: `1.5px solid ${checklistDone[ci.id] ? "#34c759" : BORDER}`, background: checklistDone[ci.id] ? "rgba(52,199,89,0.15)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {checklistDone[ci.id] && <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="#34c759" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                        </div>
                        <span style={{ fontSize: 13, color: checklistDone[ci.id] ? DIM : MUTED, textDecoration: checklistDone[ci.id] ? "line-through" : "none", lineHeight: 1.4 }}>{ci.label}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── AI Reassess button ── */}
          <button
            onClick={handleReassess}
            disabled={reassessing}
            style={{
              width: "100%", padding: "12px", borderRadius: 10, marginBottom: 10,
              border: `1px solid ${reassessing ? BORDER : ORANGE}`,
              background: "transparent",
              color: reassessing ? DIM : ORANGE,
              fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: reassessing ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {reassessing ? (
              <>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Reassessing — 15–30 seconds…
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                AI Reassess
              </>
            )}
          </button>
          {reassessError && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 10, textAlign: "center" }}>{reassessError}</div>}

          {/* ── Action buttons ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <button onClick={() => setBuyDestOpen(true)} style={{ padding: "12px 0", borderRadius: 10, border: `1px solid ${ORANGE}`, background: "transparent", color: ORANGE, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Buy</button>
            <button onClick={() => { setRemoveLoading(true); onRemove(item.id).finally(() => setRemoveLoading(false)); }} disabled={removeLoading}
              style={{ padding: "12px 0", borderRadius: 10, border: `1px solid ${BORDER}`, background: "transparent", color: DIM, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", opacity: removeLoading ? 0.5 : 1 }}>
              {removeLoading ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      )}

      {/* ── Buy destination sheet ── */}
      {buyDestOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 400, display: "flex", alignItems: "flex-end" }} onClick={() => setBuyDestOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 600, margin: "0 auto", background: SURFACE, borderRadius: "16px 16px 0 0", padding: "24px 20px 40px", borderTop: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: TEXT, marginBottom: 4 }}>Bought it</div>
            <div style={{ fontSize: 13, color: MUTED, marginBottom: 20 }}>{item.model || "Instrument"}</div>
            {/* Purchase price + date */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 10, color: DIM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Purchase Price ($)</div>
                <input
                  type="number"
                  value={buyPrice}
                  onChange={e => setBuyPrice(e.target.value)}
                  placeholder={item.asking_price ? String(item.asking_price) : "e.g. 450"}
                  style={{ ...inputSt }}
                  onClick={e => e.stopPropagation()}
                />
              </div>
              <div>
                <div style={{ fontSize: 10, color: DIM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Purchase Date</div>
                <input
                  type="date"
                  value={buyDate}
                  onChange={e => setBuyDate(e.target.value)}
                  style={{ ...inputSt }}
                  onClick={e => e.stopPropagation()}
                />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {item.rig_type ? (
                <button onClick={() => { setBuyDestOpen(false); onBuy(item, "rig", { purchase_price: buyPrice, purchase_date: buyDate }); }} style={{ width: "100%", padding: "14px 0", borderRadius: 12, border: `1px solid ${ORANGE}`, background: "transparent", color: ORANGE, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  <div>Add to Rig</div>
                  <div style={{ fontSize: 11, color: DIM, fontWeight: 400, marginTop: 2 }}>Move to your rig catalogue</div>
                </button>
              ) : (
                <>
                  <button onClick={() => { setBuyDestOpen(false); onBuy(item, "gear", { purchase_price: buyPrice, purchase_date: buyDate }); }} style={{ width: "100%", padding: "14px 0", borderRadius: 12, border: `1px solid ${ORANGE}`, background: "transparent", color: ORANGE, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    <div>My Gear</div>
                    <div style={{ fontSize: 11, color: DIM, fontWeight: 400, marginTop: 2 }}>Add to your instrument catalogue</div>
                  </button>
                  <button onClick={() => { setBuyDestOpen(false); onBuy(item, "flip", { purchase_price: buyPrice, purchase_date: buyDate }); }} style={{ width: "100%", padding: "14px 0", borderRadius: 12, border: "1px solid rgba(29,158,117,0.4)", background: "rgba(29,158,117,0.08)", color: "#1D9E75", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    <div>Start a Flip</div>
                    <div style={{ fontSize: 11, color: DIM, fontWeight: 400, marginTop: 2 }}>Move to flip workflow</div>
                  </button>
                </>
              )}
              <button onClick={() => setBuyDestOpen(false)} style={{ width: "100%", padding: "12px 0", borderRadius: 12, border: `1px solid ${BORDER}`, background: "transparent", color: DIM, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── List view ──────────────────────────────────────────────────────────────────
export default function WatchlistPage({ watchlist, onBuy, onRemove, onUpdate, onOpenGallery, accentColor, accentRgb, market }) {
  const ORANGE = accentColor || "#E8621A";
  const ACCENT_RGB = accentRgb || "232,98,26";
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", background: BG, position: "relative" }}>
      {/* Blur overlay — shows when any watchlist card is expanded */}
      {expandedId && (
        <div onClick={() => setExpandedId(null)} style={{ position: "fixed", inset: 0, zIndex: 8, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", cursor: "pointer" }} />
      )}
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ padding: "20px 20px 0" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: TEXT }}>Watchlist</div>
        </div>
        <div style={{ display: "flex", gap: 8, margin: "14px 20px 0", flexWrap: "wrap" }}>
          <button style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${ORANGE}`, background: `rgba(${ACCENT_RGB},0.12)`, color: ORANGE, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "default" }}>
            Watching{watchlist.length > 0 ? ` (${watchlist.length})` : ""}
          </button>
        </div>
        {watchlist.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <div style={{ marginBottom: 16, opacity: 0.3, display: "flex", justifyContent: "center" }}>
              <svg viewBox="0 0 24 24" width="52" height="52" fill="none" stroke={MUTED} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: MUTED, marginBottom: 6 }}>Nothing on watchlist</div>
            <div style={{ fontSize: 13, color: DIM, lineHeight: 1.6 }}>
              When you evaluate an instrument and tap Watch,<br />it appears here.
            </div>
          </div>
        ) : (
          <div style={{ padding: "16px 20px 120px" }}>
            {watchlist.map(item => (
              <div key={item.id} style={{ position: "relative", zIndex: expandedId === item.id ? 9 : undefined }}>
                <WatchlistItem
                  item={item}
                  onBuy={onBuy}
                  onRemove={onRemove}
                  onUpdate={onUpdate}
                  onOpenGallery={onOpenGallery}
                  accentColor={ORANGE}
                  accentRgb={ACCENT_RGB}
                  market={market}
                  isExpanded={expandedId === item.id}
                  onToggleExpand={() => setExpandedId(p => p === item.id ? null : item.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
